
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

import { media } from "../../server";
import { useStateContext } from "../../context/ContextProvider";

import {
  FiClock,
  FiStar,
  FiArrowLeft,
  FiArrowRight,
} from "react-icons/fi";

import { FaFireAlt } from "react-icons/fa";

function MealsCard({
  meals,
  viewMode = "grid",
  compact = false,
}) {
  const { currentDir } = useStateContext();
  const { t } = useTranslation();

  const title =
    currentDir === "ltr"
      ? meals?.titleEn
      : meals?.titleAr;

  const description =
    currentDir === "ltr"
      ? meals?.descriptionEn
      : meals?.descriptionAr;

  const categoryTitle =
    currentDir === "ltr"
      ? meals?.categoryId?.titleEn
      : meals?.categoryId?.titleAr;

  const preparationTime =
    meals?.preparationTime
      ? `${meals.preparationTime} ${t("minutes")}`
      : `20 ${t("minutes")}`;

  const DirectionArrow =
    currentDir === "rtl"
      ? FiArrowLeft
      : FiArrowRight;

  const handleScrollTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /*
   * List view
   * A separate layout is required because the old premium card
   * only supported the grid layout.
   */
  if (viewMode === "list") {
    return (
      <motion.article
        whileHover={{ y: -5 }}
        transition={{ duration: 0.35 }}
        className="
          group
          relative
          flex
          flex-col
          sm:flex-row
          overflow-hidden
          rounded-[2rem]
          border
          border-color-1/10
          bg-white
          dark:bg-back-darktertiary
          shadow-[0_16px_45px_rgba(0,0,0,0.10)]
          transition-all
          duration-500
          hover:border-color-1/30
          hover:shadow-[0_22px_60px_rgba(0,0,0,0.18)]
        "
      >
        {/* Image */}
        <div
          className="
            relative
            w-full
            h-60
            sm:w-64
            md:w-80
            sm:h-auto
            shrink-0
            overflow-hidden
          "
        >
          <img
            src={`${media}${meals?.Image}`}
            alt={title}
            className="
              w-full
              h-full
              object-cover
              transition-transform
              duration-700
              group-hover:scale-110
            "
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-black/5" />

          <div
            className="
              absolute
              top-4
              start-4
              flex
              items-center
              gap-2
              rounded-full
              border
              border-white/20
              bg-black/35
              px-4
              py-2
              text-white
              backdrop-blur-xl
              shadow-lg
            "
          >
            <FaFireAlt className="text-color-1" />

            <span className="font-kufi text-xs">
              {categoryTitle}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col justify-center p-6 md:p-8">
          <div className="flex items-start justify-between gap-4 mb-4">
            <h2
              className="
                text-2xl
                md:text-3xl
                font-kufi
                text-tcolor-4
                dark:text-white
                transition-colors
                group-hover:text-color-1
              "
            >
              {title}
            </h2>

            <div
              className="
                shrink-0
                flex
                items-center
                gap-1.5
                rounded-full
                bg-color-1/10
                px-3
                py-2
              "
            >
              <FiStar className="text-yellow-400 fill-yellow-400" />

              <span className="font-semibold text-tcolor-4 dark:text-white">
                4.9
              </span>
            </div>
          </div>

          <p className="body-2 text-n-5 dark:text-n-3 line-clamp-3 mb-5">
            {description}
          </p>

          <div className="flex items-center gap-2 text-n-5 dark:text-n-4 mb-6">
            <FiClock />

            <span className="font-kufi text-sm">
              {preparationTime}
            </span>
          </div>

          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              {meals?.price ? (
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-bold text-color-1">
                    {meals.price}
                  </span>

                  <span className="text-color-1 font-kufi mb-1">
                    {t("currency")}
                  </span>
                </div>
              ) : (
                <span className="text-color-1 font-kufi">
                  {t("today price")}
                </span>
              )}
            </div>

            <Link
              to={`/MealDetails/${meals._id}`}
              onClick={handleScrollTop}
              className="
                inline-flex
                items-center
                justify-center
                gap-3
                rounded-2xl
                bg-color-1
                px-6
                py-4
                text-white
                font-kufi
                shadow-xl
                transition-all
                duration-300
                hover:-translate-y-1
                hover:bg-color-3
              "
            >
              <span>{t("discover meal")}</span>

              <DirectionArrow />
            </Link>
          </div>
        </div>
      </motion.article>
    );
  }

  /*
   * Grid card
   * compact=true is used only when the user chooses
   * two columns on mobile.
   */
  return (
    <motion.article
      whileHover={{
        y: compact ? -5 : -12,
      }}
      transition={{ duration: 0.4 }}
      className={`
        group
        relative
        overflow-hidden
        rounded-[1.35rem]
        md:rounded-[2rem]
        shadow-[0_15px_40px_rgba(0,0,0,0.16)]
        md:shadow-[0_20px_60px_rgba(0,0,0,0.18)]
        ${
          compact
            ? "h-[340px] sm:h-[430px] md:h-[520px]"
            : "h-[480px] md:h-[520px]"
        }
      `}
    >
      {/* Image */}
      <div className="absolute inset-0">
        <img
          src={`${media}${meals?.Image}`}
          alt={title}
          className="
            w-full
            h-full
            object-cover
            transition-transform
            duration-700
            group-hover:scale-110
          "
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/65 to-black/5" />
      </div>

      {/* Category */}
      <div
        className={`
          absolute
          z-20
          ${
            compact
              ? "top-2.5 start-2.5 md:top-5 md:start-5"
              : "top-4 start-4 md:top-5 md:start-5"
          }
        `}
      >
        <div
          className={`
            flex
            max-w-full
            items-center
            gap-1.5
            rounded-full
            border
            border-white/20
            bg-white/10
            text-white
            backdrop-blur-xl
            shadow-lg
            ${
              compact
                ? "px-2 py-1 md:px-4 md:py-2"
                : "px-3 py-1.5 md:px-4 md:py-2"
            }
          `}
        >
          <FaFireAlt
            className={`
              shrink-0
              text-color-1
              ${
                compact
                  ? "text-[10px] md:text-base"
                  : "text-sm md:text-base"
              }
            `}
          />

          <span
            className={`
              max-w-[95px]
              md:max-w-[180px]
              truncate
              font-kufi
              ${
                compact
                  ? "text-[8px] md:text-sm"
                  : "text-[10px] md:text-sm"
              }
            `}
          >
            {categoryTitle}
          </span>
        </div>
      </div>

      {/* Rating */}
      <div
        className={`
          absolute
          z-20
          ${
            compact
              ? "top-2.5 end-2.5 md:top-5 md:end-5"
              : "top-4 end-4 md:top-5 md:end-5"
          }
        `}
      >
        <div
          className={`
            flex
            items-center
            gap-1
            rounded-full
            border
            border-white/10
            bg-black/40
            text-white
            backdrop-blur-xl
            shadow-lg
            ${
              compact
                ? "px-2 py-1 text-[9px] md:px-3 md:py-2 md:text-sm"
                : "px-2.5 py-1.5 text-xs md:px-3 md:py-2 md:text-sm"
            }
          `}
        >
          <FiStar className="text-yellow-400 fill-yellow-400" />

          <span className="font-semibold">
            4.9
          </span>
        </div>
      </div>

      {/* Content */}
      <div
        className={`
          absolute
          bottom-0
          start-0
          z-20
          w-full
          ${
            compact
              ? "p-2.5 md:p-6"
              : "p-4 md:p-6"
          }
        `}
      >
        <div
          className={`
            border
            border-white/10
            bg-white/10
            backdrop-blur-2xl
            ${
              compact
                ? "rounded-[1rem] p-3 md:rounded-[2rem] md:p-6"
                : "rounded-[1.5rem] p-5 md:rounded-[2rem] md:p-6"
            }
          `}
        >
          {/* Title */}
          <h2
            className={`
              font-kufi
              text-white
              line-clamp-1
              ${
                compact
                  ? "text-sm leading-6 mb-2 md:text-3xl md:mb-3"
                  : "text-xl mb-3 md:text-3xl"
              }
            `}
          >
            {title}
          </h2>

          {/* Description */}
          <p
            className={`
              text-white/80
              font-kufi
              leading-relaxed
              line-clamp-2
              ${
                compact
                  ? "hidden md:block md:body-3 md:mb-5"
                  : "text-xs mb-4 md:body-3 md:mb-5"
              }
            `}
          >
            {description}
          </p>

          {/* Price and time */}
          <div
            className={`
              flex
              items-center
              justify-between
              gap-2
              ${
                compact
                  ? "mb-2.5 md:mb-0"
                  : "mb-4 md:mb-0"
              }
            `}
          >
            <div className="min-w-0">
              {meals?.price ? (
                <div className="flex items-end gap-1">
                  <span
                    className={`
                      font-bold
                      text-color-1
                      ${
                        compact
                          ? "text-lg md:text-3xl"
                          : "text-2xl md:text-3xl"
                      }
                    `}
                  >
                    {meals.price}
                  </span>

                  <span
                    className={`
                      text-white/80
                      font-kufi
                      mb-0.5
                      ${
                        compact
                          ? "text-[8px] md:text-sm"
                          : "text-[10px] md:text-sm"
                      }
                    `}
                  >
                    {t("currency")}
                  </span>
                </div>
              ) : (
                <span
                  className={`
                    text-white/85
                    font-kufi
                    ${
                      compact
                        ? "text-[9px] md:text-sm"
                        : "text-xs md:text-sm"
                    }
                  `}
                >
                  {t("today price")}
                </span>
              )}
            </div>

            <div
              className={`
                items-center
                gap-1.5
                text-white/70
                ${
                  compact
                    ? "hidden md:flex"
                    : "flex"
                }
              `}
            >
              <FiClock />

              <span className="font-kufi text-sm">
                {preparationTime}
              </span>
            </div>
          </div>

          {/* CTA */}
          <Link
            to={`/MealDetails/${meals._id}`}
            onClick={handleScrollTop}
            aria-label={t("discover meal")}
          >
            <motion.div
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className={`
                w-full
                bg-color-1
                text-white
                flex
                items-center
                justify-center
                cursor-pointer
                shadow-xl
                transition-colors
                duration-300
                hover:bg-color-3
                ${
                  compact
                    ? "rounded-xl py-2.5 gap-1.5 md:mt-6 md:rounded-2xl md:py-4 md:gap-3"
                    : "mt-4 rounded-2xl py-3.5 gap-2 md:mt-6 md:py-4 md:gap-3"
                }
              `}
            >
              <span
                className={`
                  font-kufi
                  ${
                    compact
                      ? "text-[10px] md:text-base"
                      : "text-sm md:text-base"
                  }
                `}
              >
                {t("discover meal")}
              </span>

              <DirectionArrow
                className={`
                  ${
                    compact
                      ? "text-xs md:text-base"
                      : "text-sm md:text-base"
                  }
                `}
              />
            </motion.div>
          </Link>
        </div>
      </div>

      {/* Glow */}
      <div
        className="
          absolute
          inset-0
          opacity-0
          pointer-events-none
          transition-opacity
          duration-500
          group-hover:opacity-100
        "
      >
        <div className="absolute inset-0 rounded-[2rem] border border-color-1/40" />

        <div className="absolute -inset-1 bg-color-1/10 blur-2xl" />
      </div>
    </motion.article>
  );
}

export default MealsCard;

